import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import GitHubIcon from "@mui/icons-material/GitHub";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteIcon from "@mui/icons-material/Delete";
import { colorExport } from "../../components/common/common.Component";
import CustomDialog from "../../components-new/common/CustomDialog";
import { useNotification } from "../../hooks";
import {
  apiError,
  connectFrameworkGit,
  deleteFramework,
  getFrameworkRecipe,
  getFrameworkStatus,
  rebuildFramework,
  uploadFrameworkZip,
} from "../../API/frameworkAPI";

const POLL_MS = 4000;

const STATE_CHIP = {
  none: { label: "Not set up", color: "default" },
  building: { label: "Analysing framework…", color: "info" },
  ready: { label: "Ready", color: "success" },
  failed: { label: "Failed", color: "error" },
};

const formatTime = (ms) => (ms ? new Date(Number(ms)).toLocaleString() : "—");

function Row({ label, children }) {
  return (
    <Box sx={{ display: "flex", gap: 2, py: 0.5 }}>
      <Typography variant="body2" sx={{ width: 140, color: "text.secondary", flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" component="div" sx={{ wordBreak: "break-word" }}>
        {children}
      </Typography>
    </Box>
  );
}

export default function FrameworkSetup({ productList = [] }) {
  const colors = colorExport();
  const notify = useNotification();

  const [productId, setProductId] = useState(0);
  const [status, setStatus] = useState({ state: "none" });
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [sourceTab, setSourceTab] = useState("zip");
  const [platform, setPlatform] = useState("");

  const [zipFile, setZipFile] = useState(null);
  const [uploadPct, setUploadPct] = useState(null);
  const [git, setGit] = useState({ repoUrl: "", branch: "", gitUsername: "", gitToken: "" });
  const [busy, setBusy] = useState(false);

  const [recipe, setRecipe] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const pollRef = useRef(null);
  const state = status?.state || "none";
  const building = state === "building";

  // ------------------------------------------------------------ status
  const loadStatus = useCallback(
    async (quiet = false) => {
      if (!quiet) setLoadingStatus(true);
      try {
        const s = await getFrameworkStatus(productId);
        setStatus(s || { state: "none" });
        return s;
      } catch (err) {
        if (!quiet) notify(apiError(err, "Could not load framework status"), "error");
        return null;
      } finally {
        if (!quiet) setLoadingStatus(false);
      }
    },
    [productId, notify],
  );

  useEffect(() => {
    setRecipe(null);
    setShowDetails(false);
    loadStatus();
  }, [productId]); // eslint-disable-line react-hooks/exhaustive-deps

  // poll while building; announce the result once
  useEffect(() => {
    clearInterval(pollRef.current);
    if (!building) return undefined;
    pollRef.current = setInterval(async () => {
      const s = await loadStatus(true);
      if (s && s.state !== "building") {
        clearInterval(pollRef.current);
        if (s.state === "ready") notify("Framework analysed — code generation is ready", "success");
        if (s.state === "failed") notify(s.error || "Framework analysis failed", "error");
      }
    }, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [building, loadStatus, notify]);

  // ------------------------------------------------------------ actions
  const startedBuild = (s) => {
    setStatus(s || { state: "building" });
    setRecipe(null);
    setShowDetails(false);
    notify("Upload complete — analysing your framework. This usually takes 1–3 minutes.", "info");
  };

  const onUpload = async () => {
    if (!zipFile) return;
    if (!zipFile.name.toLowerCase().endsWith(".zip")) {
      notify("Please choose a .zip file", "error");
      return;
    }
    setBusy(true);
    setUploadPct(0);
    try {
      startedBuild(await uploadFrameworkZip(productId, zipFile, platform, setUploadPct));
      setZipFile(null);
    } catch (err) {
      notify(apiError(err, "Upload failed"), "error");
    } finally {
      setBusy(false);
      setUploadPct(null);
    }
  };

  const onConnectGit = async () => {
    if (!/^https:\/\/\S+$/i.test(git.repoUrl.trim())) {
      notify("Enter an https Git URL, e.g. https://github.com/acme/qa-framework.git", "error");
      return;
    }
    setBusy(true);
    try {
      startedBuild(await connectFrameworkGit(productId, { ...git, repoUrl: git.repoUrl.trim(), platform }));
      setGit((g) => ({ ...g, gitToken: "" })); // never keep the token in the form
    } catch (err) {
      notify(apiError(err, "Could not clone the repository"), "error");
    } finally {
      setBusy(false);
    }
  };

  const onRebuild = async () => {
    setBusy(true);
    try {
      setStatus(await rebuildFramework(productId));
      setRecipe(null);
      notify("Re-analysing with the currently selected AI model…", "info");
    } catch (err) {
      notify(apiError(err, "Could not start the rebuild"), "error");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    setConfirmDelete(false);
    setBusy(true);
    try {
      await deleteFramework(productId);
      setStatus({ state: "none" });
      setRecipe(null);
      notify("Framework removed", "success");
    } catch (err) {
      notify(apiError(err, "Could not remove the framework"), "error");
    } finally {
      setBusy(false);
    }
  };

  const toggleDetails = async () => {
    if (showDetails) {
      setShowDetails(false);
      return;
    }
    if (!recipe) {
      try {
        setRecipe(await getFrameworkRecipe(productId));
      } catch (err) {
        notify(apiError(err, "Could not load the framework details"), "error");
        return;
      }
    }
    setShowDetails(true);
  };

  // ------------------------------------------------------------ render
  const chip = STATE_CHIP[state] || STATE_CHIP.none;
  const hasSource = state !== "none";
  const productName =
    productId === 0
      ? "All products (company default)"
      : productList.find((p) => p.idproducts === productId)?.productname || `Product ${productId}`;

  return (
    <div className="card" style={{ backgroundColor: colors.tertiaryColor }}>
      <h3>Framework (Code Generation)</h3>
      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        Upload your own automation framework. NoGrunt studies how it is written — page objects,
        base classes, helpers, naming, language — and generates code from your recorded tests in
        exactly that style. Works for web and mobile, any language.
      </Typography>

      {/* ---------------- product ---------------- */}
      <Card sx={{ p: 2, mb: 2 }}>
        <FormControl fullWidth size="small">
          <InputLabel id="fw-product">Product</InputLabel>
          <Select
            labelId="fw-product"
            label="Product"
            value={productId}
            onChange={(e) => setProductId(Number(e.target.value))}
            disabled={busy}
          >
            <MenuItem value={0}>
              <em>All products (company default)</em>
            </MenuItem>
            {productList.map((p) => (
              <MenuItem key={p.idproducts} value={p.idproducts}>
                {p.productname}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary" }}>
          A product without its own framework uses the company default.
        </Typography>
      </Card>

      {/* ---------------- status ---------------- */}
      <Card sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Status — {productName}
          </Typography>
          <Chip label={chip.label} color={chip.color} size="small" />
        </Box>
        {(building || loadingStatus) && <LinearProgress sx={{ mb: 1 }} />}

        {state === "none" && !loadingStatus && (
          <Typography variant="body2" color="text.secondary">
            No framework uploaded yet. Add one below.
          </Typography>
        )}

        {hasSource && (
          <>
            {status.summary && <Row label="Framework">{status.summary}</Row>}
            {status.language && (
              <Row label="Language / platform">
                {status.language}
                {status.platform ? ` · ${status.platform}` : ""}
              </Row>
            )}
            {status.sourceRef && (
              <Row label="Source">
                {status.sourceType === "git" ? "Git: " : "Zip: "}
                {status.sourceRef}
              </Row>
            )}
            {status.model && <Row label="Analysed with">{status.model}</Row>}
            {status.confidence != null && (
              <Row label="Confidence">{Math.round(Number(status.confidence) * 100)}%</Row>
            )}
            <Row label="Last updated">{formatTime(status.builtAt || status.updatedAt)}</Row>
          </>
        )}

        {state === "failed" && status.error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {status.error}
          </Alert>
        )}
        {state === "ready" && (
          <Alert severity="info" sx={{ mt: 1 }}>
            To use it, set the product's code generation type to <b>Framework</b>. Then use
            Generate Code on any test case — you'll get a zip to extract over your project.
          </Alert>
        )}

        {hasSource && (
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 2, flexWrap: "wrap" }}>
            {state === "ready" && (
              <Button size="small" variant="outlined" onClick={toggleDetails}>
                {showDetails ? "Hide details" : "What NoGrunt understood"}
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={onRebuild}
              disabled={busy || building}
            >
              Re-analyse
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setConfirmDelete(true)}
              disabled={busy || building}
            >
              Remove
            </Button>
          </Box>
        )}

        <Collapse in={showDetails && !!recipe} unmountOnExit>
          {recipe && <RecipeDetails recipe={recipe} />}
        </Collapse>
      </Card>

      {/* ---------------- source ---------------- */}
      <Card sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          {hasSource ? "Replace framework" : "Add framework"}
        </Typography>
        <Tabs value={sourceTab} onChange={(_, v) => setSourceTab(v)} sx={{ mb: 2 }}>
          <Tab value="zip" label="Upload zip" icon={<CloudUploadIcon />} iconPosition="start" />
          <Tab value="git" label="Git repository" icon={<GitHubIcon />} iconPosition="start" />
        </Tabs>

        <FormControl size="small" sx={{ minWidth: 260, mb: 2 }}>
          <InputLabel id="fw-platform">Tests in this framework are for</InputLabel>
          <Select
            labelId="fw-platform"
            label="Tests in this framework are for"
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            disabled={busy}
          >
            <MenuItem value="">Detect automatically</MenuItem>
            <MenuItem value="web">Web</MenuItem>
            <MenuItem value="mobile">Mobile</MenuItem>
          </Select>
        </FormControl>

        {sourceTab === "zip" && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Button variant="outlined" component="label" disabled={busy || building}>
                Choose .zip
                <input
                  hidden
                  type="file"
                  accept=".zip,application/zip"
                  onChange={(e) => setZipFile(e.target.files?.[0] || null)}
                  onClick={(e) => {
                    e.target.value = null; // allow re-selecting the same file
                  }}
                />
              </Button>
              <Typography variant="body2" color="text.secondary">
                {zipFile ? `${zipFile.name} (${(zipFile.size / (1024 * 1024)).toFixed(1)} MB)` : "No file chosen"}
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary" }}>
              Zip the framework project folder (source only — leave out build output such as
              target/, node_modules/, bin/). Up to 300 MB.
            </Typography>
            {uploadPct != null && (
              <LinearProgress variant="determinate" value={uploadPct} sx={{ mt: 2 }} />
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button
                size="small"
                variant="contained"
                onClick={onUpload}
                disabled={!zipFile || busy || building}
              >
                Upload & analyse
              </Button>
            </Box>
          </Box>
        )}

        {sourceTab === "git" && (
          <Box sx={{ display: "grid", gap: 2 }}>
            <TextField
              size="small"
              label="Repository URL (https)"
              placeholder="https://github.com/acme/qa-framework.git"
              value={git.repoUrl}
              onChange={(e) => setGit({ ...git, repoUrl: e.target.value })}
              fullWidth
            />
            <TextField
              size="small"
              label="Branch (optional)"
              placeholder="main"
              value={git.branch}
              onChange={(e) => setGit({ ...git, branch: e.target.value })}
              fullWidth
            />
            <Divider textAlign="left">
              <Typography variant="caption" color="text.secondary">
                Private repository
              </Typography>
            </Divider>
            <TextField
              size="small"
              label="Username (optional)"
              value={git.gitUsername}
              onChange={(e) => setGit({ ...git, gitUsername: e.target.value })}
              fullWidth
            />
            <TextField
              size="small"
              type="password"
              label="Access token"
              helperText="Read-only token. Used once to clone and never stored."
              value={git.gitToken}
              onChange={(e) => setGit({ ...git, gitToken: e.target.value })}
              autoComplete="new-password"
              fullWidth
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                size="small"
                variant="contained"
                onClick={onConnectGit}
                disabled={!git.repoUrl.trim() || busy || building}
              >
                Clone & analyse
              </Button>
            </Box>
          </Box>
        )}
      </Card>

      <CustomDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Remove framework?"
        width="440px"
        content={
          <Typography variant="body2">
            Code generation for <b>{productName}</b> will stop using your framework style until
            you upload it again.
          </Typography>
        }
        actions={
          <>
            <Button onClick={() => setConfirmDelete(false)} color="inherit">
              Cancel
            </Button>
            <Button onClick={onDelete} variant="contained" color="error">
              Remove
            </Button>
          </>
        }
      />
    </div>
  );
}

/** Human-readable view of the recipe: what NoGrunt will follow when generating code. */
function RecipeDetails({ recipe }) {
  const [showRaw, setShowRaw] = useState(false);
  const pages = recipe?.reuse?.pages || [];
  const elementCount = pages.reduce((n, p) => n + (p.elements?.length || 0), 0);
  const po = recipe?.page_objects || {};
  const layout = recipe?.layout || {};
  const actions = Object.keys(recipe?.actions || {}).filter((a) => a !== "comment");
  const locators = Object.keys(recipe?.locators || {});

  return (
    <Box sx={{ mt: 2 }}>
      <Divider sx={{ mb: 2 }} />
      <Row label="Library / runner">
        {[recipe.automation_library, recipe.test_runner].filter(Boolean).join(" · ") || "—"}
      </Row>
      <Row label="Page objects">
        {po.used
          ? `Yes — actions go in ${po.actions_live_in === "page_methods" ? "page methods" : "the test body"}`
          : "No — steps are written directly in tests"}
      </Row>
      <Row label="New tests go to">{layout.tests_dir || "—"}</Row>
      {po.used && <Row label="New pages go to">{layout.pages_dir || "—"}</Row>}
      <Row label="Existing pages found">
        {pages.length} page(s), {elementCount} element(s) — reused when a recorded locator matches
      </Row>
      {recipe?.reuse?.base_test_class && <Row label="Base test class">{recipe.reuse.base_test_class}</Row>}
      {recipe?.reuse?.base_page_class && <Row label="Base page class">{recipe.reuse.base_page_class}</Row>}
      <Row label="Locators">{locators.join(", ") || "—"}</Row>
      <Row label="Actions">{actions.length} supported</Row>

      {Array.isArray(recipe.conventions) && recipe.conventions.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.5 }}>
            Conventions it will follow
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {recipe.conventions.map((c, i) => (
              <li key={i}>
                <Typography variant="body2">{c}</Typography>
              </li>
            ))}
          </ul>
        </Box>
      )}

      <Button size="small" sx={{ mt: 1 }} onClick={() => setShowRaw((v) => !v)}>
        {showRaw ? "Hide technical recipe" : "Show technical recipe"}
      </Button>
      <Collapse in={showRaw} unmountOnExit>
        <Box
          component="pre"
          sx={{
            mt: 1,
            p: 1.5,
            maxHeight: 360,
            overflow: "auto",
            fontSize: 12,
            bgcolor: "#f6f8fa",
            borderRadius: 1,
          }}
        >
          {JSON.stringify(recipe, null, 2)}
        </Box>
      </Collapse>
    </Box>
  );
}
